/**
 * Ряды строк «Голоса клиента» на экране (#2394 §9 п. 7, спека
 * docs/plans/2026-10-19-voc-trends-ui.md) — без React: какой ряд у строки,
 * какими запросами его просить, точки спарклайна, покрытие блока и порядок
 * вида «Что изменилось».
 *
 * Числа ряда считает сервер (`GET /analytics/voice/series`, п. 5): корзина,
 * которую факты не покрывают, приходит `null` — пусто, не ноль. Экран своего
 * правила не добавляет: доля точки — K/N той же корзины.
 */
import {
  VOICE_TREND_MAX_KEYS,
  cutVocSignalKey,
  type VocFactSignal,
  type VoiceTrendBucket,
  type VoiceTrendResponse,
} from '@aica/api-types/schemas';
import { UNMAPPED_OBJECTION_CODE } from '@aica/core/voc';
import { DAY_MS, dayParts } from '../objections/objections-search';
import type { VoiceSearch } from './voice-search';
import type { VoiceRow, VoiceRowKind, VoiceSection } from './voice-view-model';

/** Окно экрана до стольких дней — ряд по дням, длиннее — по неделям (окно раздела ≤ 93 дней). */
export const VOICE_SERIES_DAILY_MAX_DAYS = 31;

/** Вид «12 месяцев»: месяц конца окна экрана и 11 полных перед ним — в пределах горизонта ряда. */
export const VOICE_SERIES_YEAR_MONTHS = 12;

export type VoiceSeriesUnit = 'day' | 'week' | 'month';

/**
 * Корзина ряда у окна экрана: день до `VOICE_SERIES_DAILY_MAX_DAYS`, дальше —
 * неделя. Дни — календарные: месяц с переводом часов длиннее 31 × 24 ч на час.
 */
export function seriesUnitFor(periodStart: string, periodEnd: string): 'day' | 'week' {
  const days = Math.round((Date.parse(periodEnd) - Date.parse(periodStart)) / DAY_MS);
  return days <= VOICE_SERIES_DAILY_MAX_DAYS ? 'day' : 'week';
}

/** Полсуток — насколько полночь окна в чужом поясе отстоит от местной. */
const HALF_DAY_MS = DAY_MS / 2;

/**
 * Окно вида «12 месяцев» — местные месяцы с первого числа за 11 месяцев до
 * месяца, в котором лежит последний день окна экрана, по конец этого месяца
 * или окна, что раньше: 11 полных месяцев и месяц конца, а не 365 дней с
 * середины месяца (13 неполных корзин). Местное — как у окна экрана: его
 * период считается в поясе браузера, и ряд просит тот же.
 *
 * `lastDay` — последний календарный день окна в его собственном поясе (окно
 * организации из письма или карточки, `windowLastDay`): месяц — по нему.
 * Без него — по местному дню полсуток до конца окна: у окна в поясе браузера
 * это и есть его последний день.
 */
export function yearWindow(
  periodStart: string,
  periodEnd: string,
  lastDay?: string
): {
  periodStart: string;
  periodEnd: string;
} {
  const end = Date.parse(periodEnd);
  const day = dayParts(lastDay);
  const lastStart = day && new Date(day[0], day[1] - 1, day[2]);
  // A zone a day or more west of the organization's: its last day starts here
  // only after the window has ended — the month is the one the end falls in.
  const last = lastStart
    ? new Date(Math.min(lastStart.getTime(), end))
    : new Date(end - Math.min(HALF_DAY_MS, (end - Date.parse(periodStart)) / 2));
  const start = new Date(
    last.getFullYear(),
    last.getMonth() - (VOICE_SERIES_YEAR_MONTHS - 1),
    1
  );
  // The organization's window ends in another zone: east of the viewer its
  // last evening is already the next month here. The request stops at the
  // local month's end — a 13th, partial bucket otherwise, and on a leap span
  // more days than the series allows.
  const monthEnd = new Date(last.getFullYear(), last.getMonth() + 1, 1).getTime() - 1;
  return {
    periodStart: start.toISOString(),
    periodEnd: new Date(Math.min(end, monthEnd)).toISOString(),
  };
}

/**
 * Сигнал фактов у вида строки. Без сигнала — строки, чьего счёта в фактах
 * нет: потребности (ключа нет), без ответа (доля среди разговоров с
 * вопросом), первая минута (у фактов нет минуты).
 */
const ROW_SIGNAL: Partial<Record<VoiceRowKind, VocFactSignal>> = {
  competitor: 'competitor',
  feedback: 'product_feedback',
  signal: 'market_signal',
  question: 'question_subject',
  decline: 'decline_reason',
  theme: 'theme',
  noTheme: 'theme',
  objection: 'objection',
};

/** Виды, у которых ключ строки и есть ключ ряда; у конкурента и темы — свой (`seriesKey`). */
const KEY_IS_SERIES_KEY: ReadonlySet<VoiceRowKind> = new Set([
  'feedback',
  'signal',
  'question',
  'decline',
  'objection',
]);

/** Ряд строки: сигнал фактов и ключ — как `resolved_key`. */
export interface VoiceSeriesRef {
  signal: VocFactSignal;
  key: string;
}

/** `null` — у строки ряда нет (вид без счёта в фактах, нет ключа конкурента). */
export function rowSeriesRef(
  row: Pick<VoiceRow, 'kind' | 'key' | 'seriesKey'>
): VoiceSeriesRef | null {
  const signal = ROW_SIGNAL[row.kind];
  if (!signal) return null;
  const key = row.seriesKey ?? (KEY_IS_SERIES_KEY.has(row.kind) ? row.key : undefined);
  // Cut by the writer's rule: a long key is stored cut, and asked so.
  return key ? { signal, key: cutVocSignalKey(key) } : null;
}

/** Ряд кода потока возражений; `null` — у нераспознанных ряда нет. */
export function objectionSeriesRef(code: string): VoiceSeriesRef | null {
  return code === UNMAPPED_OBJECTION_CODE
    ? null
    : { signal: 'objection', key: cutVocSignalKey(code) };
}

/** Один запрос ряда: сигнал и до `VOICE_TREND_MAX_KEYS` ключей. */
export interface VoiceSeriesRequest {
  signal: VocFactSignal;
  keys: string[];
}

/** Строка блока, у которой может быть ряд: K окна и прошлого окна. */
export interface VoiceSeriesRow {
  ref: VoiceSeriesRef | null;
  calls: number;
  prevCalls: number;
}

/**
 * Запросы рядов для строк: по сигналу, ключи — строк, у которых были
 * разговоры в окне или в прошлом окне, по порядку ключей, кусками по
 * `VOICE_TREND_MAX_KEYS`. Строка «исчезло» (K = 0 сейчас) ряд просит: её
 * нули в окне — то, что она показывает, а вид «12 месяцев» — её историю.
 * Строка без разговоров в обоих окнах ряда не просит: её корзины окна — нули;
 * кроме темы — она могла появиться внутри окна, и корзины до её начала пусты
 * (`null`), а не нули: это знает только сервер.
 */
export function seriesRequests(rows: readonly VoiceSeriesRow[]): VoiceSeriesRequest[] {
  const keysBySignal = new Map<VocFactSignal, string[]>();
  for (const { ref, calls, prevCalls } of rows) {
    if (!ref || (calls === 0 && prevCalls === 0 && ref.signal !== 'theme')) continue;
    const keys = keysBySignal.get(ref.signal) ?? [];
    if (!keys.includes(ref.key)) keys.push(ref.key);
    keysBySignal.set(ref.signal, keys);
  }
  // Sorted: the same keys in another order (a re-sorted block) are the same
  // requests, and the cache answers them.
  return [...keysBySignal].flatMap(([signal, unsorted]) => {
    const keys = [...unsorted].sort();
    const chunks: VoiceSeriesRequest[] = [];
    for (let at = 0; at < keys.length; at += VOICE_TREND_MAX_KEYS) {
      chunks.push({ signal, keys: keys.slice(at, at + VOICE_TREND_MAX_KEYS) });
    }
    return chunks;
  });
}

const askedKey = (signal: VocFactSignal, key: string) => `${signal}\u0000${key}`;

/**
 * Пачки запросов блока, накопленные за время показа: уже спрошенные ключи
 * остаются в своих пачках (тот же ключ кеша), новые строки — «Показать все
 * N», следующая страница — идут новой пачкой, а не пересобирают весь запрос
 * и не пересчитывают на сервере ряды, которые уже пришли. Нового нет — та же
 * пачка (`previous`).
 */
export function seriesBatches(
  previous: readonly VoiceSeriesRequest[],
  rows: readonly VoiceSeriesRow[]
): readonly VoiceSeriesRequest[] {
  const asked = new Set(
    previous.flatMap((batch) => batch.keys.map((key) => askedKey(batch.signal, key)))
  );
  const fresh = seriesRequests(
    rows.filter((row) => row.ref && !asked.has(askedKey(row.ref.signal, row.ref.key)))
  );
  return fresh.length === 0 ? previous : [...previous, ...fresh];
}

/** Пачки, в которых есть ключ нужной сейчас строки, — их и просить. */
export function batchesFor(
  batches: readonly VoiceSeriesRequest[],
  rows: readonly VoiceSeriesRow[]
): VoiceSeriesRequest[] {
  const needed = new Set(
    seriesRequests(rows).flatMap((request) =>
      request.keys.map((key) => askedKey(request.signal, key))
    )
  );
  return batches.filter((batch) =>
    batch.keys.some((key) => needed.has(askedKey(batch.signal, key)))
  );
}

/** Ответы кусков одного сигнала — одним рядом: корзины одни, строки — вместе. */
export function mergeSeries(
  parts: readonly VoiceTrendResponse[]
): VoiceTrendResponse | null {
  const [first, ...rest] = parts;
  if (!first) return null;
  return rest.reduce<VoiceTrendResponse>(
    (merged, part) => ({
      ...merged,
      rows: [...merged.rows, ...part.rows],
      breaks: [...new Set([...merged.breaks, ...part.breaks])].sort((a, b) => a - b),
      themeBreaks: [
        ...new Set([...(merged.themeBreaks ?? []), ...(part.themeBreaks ?? [])]),
      ].sort((a, b) => a - b),
      resolutionStale: merged.resolutionStale || part.resolutionStale,
    }),
    first
  );
}

/** Ряд одной строки: корзины ответа и её K по ним. */
export interface VoiceRowSeries {
  buckets: VoiceTrendBucket[];
  /** K по корзинам; `null` — корзина не покрыта. */
  counts: (number | null)[];
  /** Номера корзин, с которых сменилась версия набора кодов или чтение темы. */
  breaks: number[];
}

/** Ряд раскрытой строки (#2394 §9 п. 7): график и выбор корзины над разговорами. */
export interface VoiceRowDetailsTrend {
  seriesRef: VoiceSeriesRef;
  series: VoiceRowSeries;
  unit: VoiceSeriesUnit;
  /**
   * What «Show this month» changes besides the period: from «What changed» —
   * the row's own section, where the row stays whatever the month marks.
   */
  monthPatch?: Partial<VoiceSearch>;
}

/**
 * Ряд строки из ответа её сигнала. Строка с K = 0 ряда не просила — её
 * корзины нули там, где ответ их покрывает. Кроме темы: тема могла появиться
 * внутри окна, и нули до неё выдали бы несчитанное за «не говорили». Строки
 * нет в ответе (ключ не совпал) — ряда нет, а не нули.
 *
 * Ответ, сведённый по другой версии ключей (`resolutionStale`), ключу без
 * фактов отдаёт нули: строка с разговорами и рядом из одних нулей — ключ,
 * которого в фактах ещё нет (новое имя глоссария), а не «0 %». Ряда нет.
 */
export function rowSeries(
  response: VoiceTrendResponse | null | undefined,
  ref: VoiceSeriesRef | null,
  calls: number
): VoiceRowSeries | null {
  if (!response || !ref) return null;
  const row = response.rows.find((candidate) => candidate.key === ref.key);
  if (
    row &&
    response.resolutionStale &&
    calls > 0 &&
    row.counts.every((count) => (count ?? 0) === 0)
  ) {
    return null;
  }
  const counts = row
    ? row.counts
    : calls === 0 && ref.signal !== 'theme'
      ? response.buckets.map((bucket) => (bucket.covered ? 0 : null))
      : null;
  if (!counts) return null;
  // A line breaks for either reason; the note under it names them apart.
  const breaks = [...new Set([...response.breaks, ...(response.themeBreaks ?? [])])];
  return { buckets: response.buckets, counts, breaks: breaks.sort((a, b) => a - b) };
}

/**
 * Ряд строки из кусков области — у читателя, который своего запроса не шлёт
 * (карточка сводки). `asked` — куски, которые открытый блок просит сейчас;
 * `rest` — остальные пришедшие: старые пачки «Показать все», «Что изменилось»,
 * закрытый блок. В обоих новые первыми: строка в двух кусках берётся из более
 * позднего, вместе с корзинами, доля которых она. Строка в просимом куске — разрывы и
 * устарелость всех просимых, как у блока: старая пачка не скроет линию,
 * которую он рисует. Иначе никто её не просит — только её кусок: сервер
 * считает их по всему сигналу, и куски отличаются лишь временем ответа.
 */
export function loadedRowSeries(
  asked: readonly VoiceTrendResponse[],
  rest: readonly VoiceTrendResponse[],
  ref: VoiceSeriesRef | null,
  calls: number
): VoiceRowSeries | null {
  if (!ref) return null;
  const holdsRow = (part: VoiceTrendResponse) =>
    part.rows.some((row) => row.key === ref.key);
  const own = asked.find(holdsRow) ?? rest.find(holdsRow);
  const merged = own ? mergeSeries(asked.includes(own) ? asked : [own]) : null;
  return own && merged
    ? rowSeries(
        {
          ...own,
          breaks: merged.breaks,
          themeBreaks: merged.themeBreaks,
          resolutionStale: merged.resolutionStale,
        },
        ref,
        calls
      )
    : null;
}

/** Доля корзины в процентах; пусто — корзина не покрыта или в ней нет разговоров блока. */
export function seriesShares(series: VoiceRowSeries): (number | null)[] {
  return series.counts.map((count, index) => {
    const calls = series.buckets[index]?.calls ?? null;
    return count === null || calls === null || calls === 0 ? null : (count / calls) * 100;
  });
}

/**
 * Пусты ли у какой-нибудь темы покрытые корзины — тема появилась внутри окна,
 * и до её начала её не считали (сервер: корзина до базы темы — `null`).
 */
export function seriesThemeGaps(
  response: VoiceTrendResponse | null | undefined
): boolean {
  if (!response) return false;
  return response.rows.some((row) =>
    row.counts.some((count, index) => count === null && response.buckets[index]?.covered)
  );
}

/** Есть ли у ряда хоть одна точка — иначе спарклайна нет, говорит строка покрытия. */
export function seriesHasPoints(series: VoiceRowSeries): boolean {
  return series.counts.some(
    (count, index) => count !== null && (series.buckets[index]?.calls ?? 0) > 0
  );
}

/**
 * Покрытие ряда блока — для строки над строками (§1 спеки):
 * - `none` — ни одна начавшаяся корзина не покрыта (на проде до заполнения
 *   истории);
 * - `partial` — часть начавшихся корзин пуста (до первой записи, ещё не
 *   заполнено);
 * - `full` — все начавшиеся корзины покрыты.
 * Корзина в будущем (окно «этот месяц») не покрыта по определению и в счёт
 * не идёт.
 */
export type VoiceSeriesCoverage = 'full' | 'partial' | 'none';

export function seriesCoverage(
  buckets: readonly Pick<VoiceTrendBucket, 'start' | 'covered'>[],
  now: number = Date.now()
): VoiceSeriesCoverage {
  const started = buckets.filter((bucket) => Date.parse(bucket.start) <= now);
  const covered = started.filter((bucket) => bucket.covered).length;
  if (covered === 0) return 'none';
  return covered === started.length ? 'full' : 'partial';
}

/** Строка вида «Что изменилось» — строка блока с названием блока. */
export interface VoiceChangeRow {
  row: VoiceRow;
  section: VoiceSection['id'];
}

/**
 * Блок вкладки в виде «Что изменилось»: его строки, загрузка или сбой с
 * повтором — у каждого свой: упавший блок не прячет пометки готовых.
 */
export type VoiceChangesBlock =
  | {
      status: 'ready';
      sections: readonly VoiceSection[];
      /** Its counts are a capped scan's lower bound (`VoiceSeriesNote`, #2596). */
      lowerBound: boolean;
    }
  | { status: 'pending' }
  | { status: 'failed'; retry: () => void };

/**
 * Блок из ответа вкладки — как его рисует свой раздел: ответ без блоков
 * (сервер их не собрал) и сбой обновления поверх прошлого ответа — сбой с
 * повтором, а не текущие строки.
 */
export function changesBlock<T extends { truncated: boolean }>(
  answer: { isPending: boolean; isError: boolean; refetch: () => unknown },
  ready: T | undefined,
  sections: (ready: T) => VoiceSection[]
): VoiceChangesBlock {
  if (answer.isPending) return { status: 'pending' };
  if (ready === undefined || answer.isError) {
    return { status: 'failed', retry: () => void answer.refetch() };
  }
  return { status: 'ready', sections: sections(ready), lowerBound: ready.truncated };
}

/** Доля строки в долях единицы; N = 0 — ноль. */
const shareOf = (calls: number, callsInWindow: number): number =>
  callsInWindow > 0 ? calls / callsInWindow : 0;

/**
 * Значимость пометки — модуль сдвига доли: у «выросло» и «упало» — между
 * окнами, у «новое» — нынешняя доля, у «исчезло» — прошлая (вторая доля —
 * ноль).
 */
export function changeWeight(row: VoiceRow): number {
  const now = shareOf(row.calls, row.callsInWindow);
  const before = shareOf(row.prevCalls, row.prevCallsInWindow);
  switch (row.mark?.kind) {
    case 'new':
      return now;
    case 'gone':
      return before;
    default:
      return Math.abs(now - before);
  }
}

/**
 * Строки всех блоков вкладки с пометкой Р3 — по значимости, затем по K.
 * Пометку ставит сервер (`mark`); экран её не выводит сам.
 */
export function changeRows(sections: readonly VoiceSection[]): VoiceChangeRow[] {
  return sections
    .flatMap((section) =>
      section.rows.filter((row) => row.mark).map((row) => ({ row, section: section.id }))
    )
    .sort(
      (a, b) =>
        changeWeight(b.row) - changeWeight(a.row) ||
        b.row.calls - a.row.calls ||
        b.row.prevCalls - a.row.prevCalls
    );
}

/** Точка спарклайна в его координатах. */
export interface SparkPoint {
  x: number;
  y: number;
}

/**
 * Отрезки линии спарклайна: точка — корзина с долей, разрыв — пустая корзина
 * (`null`) и корзина, с которой сменилась версия кодов. Ось Y — от нуля до
 * наибольшей доли ряда (не меньше 1 %: ряд из одних нулей — линия по низу).
 */
export function sparklineSegments(
  shares: readonly (number | null)[],
  breaks: readonly number[],
  width: number,
  height: number
): SparkPoint[][] {
  const top = Math.max(1, ...shares.map((share) => share ?? 0));
  const step = shares.length > 1 ? width / (shares.length - 1) : 0;
  const segments: SparkPoint[][] = [];
  let current: SparkPoint[] = [];
  shares.forEach((share, index) => {
    if (share === null || breaks.includes(index)) {
      if (current.length > 0) segments.push(current);
      current = [];
    }
    if (share === null) return;
    current.push({
      x: shares.length > 1 ? index * step : width / 2,
      y: height - (share / top) * height,
    });
  });
  if (current.length > 0) segments.push(current);
  return segments;
}

/**
 * Корзина словами на языке смотрящего: день — «3 окт.», неделя — «с 30
 * сент.», месяц — «окт. 2026». Метка корзины — местный день `YYYY-MM-DD`,
 * поэтому форматируется в UTC: пояс смотрящего его уже разрезал.
 */
export function bucketPeriodText(
  bucket: Pick<VoiceTrendBucket, 'label'>,
  unit: VoiceSeriesUnit,
  locale: string,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  const date = new Date(`${bucket.label}T00:00:00.000Z`);
  if (unit === 'month') {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  }
  const day = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(date);
  return unit === 'week' ? t('voicePage.series.weekOf', { day }) : day;
}

/**
 * Номер выбранной корзины по её началу; `null` — не выбрана или её больше нет
 * (окно сменилось: другие корзины, и выбор по номеру указал бы на чужую).
 */
export function bucketIndex(
  buckets: readonly Pick<VoiceTrendBucket, 'start'>[],
  start: string | null
): number | null {
  if (start === null) return null;
  const index = buckets.findIndex((bucket) => bucket.start === start);
  return index === -1 ? null : index;
}

/** Разговоры строки, попавшие в корзину; список строки — последние разговоры окна. */
export function itemsInBucket<T extends { createdAt: string }>(
  items: readonly T[],
  bucket: Pick<VoiceTrendBucket, 'start' | 'end'>
): T[] {
  const start = Date.parse(bucket.start);
  const end = Date.parse(bucket.end);
  return items.filter((item) => {
    const at = Date.parse(item.createdAt);
    return start <= at && at < end;
  });
}

/** Процент для подписи — целый; меньше процента, но не ноль — «< 1». */
const percentText = (share: number): string =>
  share > 0 && share < 1 ? '< 1' : String(Math.round(share));

/** Доля корзины для подписи (`percentText`). */
export function bucketShareText(count: number, calls: number): string {
  return calls === 0 ? '0' : percentText((count / calls) * 100);
}

/**
 * Что показывает спарклайн — словами, для чтения экраном и подсказки:
 * корзина и доли крайних точек ряда («по дням: с 4 % до 12 %»).
 */
export function sparklineLabel(
  shares: readonly (number | null)[],
  unit: VoiceSeriesUnit,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  const points = shares.filter((share): share is number => share !== null);
  return t(`voicePage.series.sparkLabel.${unit}`, {
    first: percentText(points[0] ?? 0),
    last: percentText(points[points.length - 1] ?? 0),
  });
}
